/**
 * useKitchenActions — sends a kitchen action with an Idempotency-Key minted
 * before the first attempt, queues it on a transport failure, and retries the
 * queue when the connection comes back (online event, every 30s, and on
 * demand). See offlineQueue.ts for why this is safe.
 *
 * Each queued action carries who saved it; only the signed-in person's are
 * sent. Actions that kept failing on the server, and other people's, are
 * returned (failed / held) for the screen to show with Try again / Discard.
 */
import { useCallback, useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import {
  backoffMs,
  currentQueueUser,
  enqueueAction,
  flushQueue,
  isRetryableFailure,
  loadQueue,
  planRun,
  removeAction,
  retryAction,
  splitQueue,
  type FailureLike,
  type QueuedAction,
  type QueuedMethod,
  type RunOptions,
} from './offlineQueue'

export type KitchenActionResult<T> = { status: 'sent'; data: T } | { status: 'queued' }

export function useKitchenActions(
  onRefused?: (label: string, message: string) => void,
  onGaveUp?: (label: string) => void,
) {
  const qc = useQueryClient()
  const [pending, setPending] = useState<QueuedAction[]>(() => loadQueue())

  const invalidate = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['inventory-flow'] })
  }, [qc])

  const flushWith = useCallback(async (force: boolean) => {
    if (loadQueue().length === 0) return
    const user = currentQueueUser()
    const res = await flushQueue((a) => apiClient.inventoryFlowMutate(a.method, a.path, a.body, a.id), undefined, {
      userId: user?.id ?? '',
      force,
    })
    setPending(loadQueue())
    if (res.sent.length > 0 || res.refused.length > 0) invalidate()
    for (const r of res.refused) onRefused?.(r.action.label, r.message)
    for (const f of res.failed) onGaveUp?.(f.label)
  }, [invalidate, onRefused, onGaveUp])

  /** Background retry: respects the wait after a server error. */
  const flush = useCallback(() => flushWith(false), [flushWith])
  /** "Try again now": sends what is due and what is still waiting. */
  const flushNow = useCallback(() => flushWith(true), [flushWith])

  useEffect(() => {
    const onOnline = () => void flush()
    window.addEventListener('online', onOnline)
    const id = window.setInterval(() => void flush(), 30_000)
    void flush()
    return () => {
      window.removeEventListener('online', onOnline)
      window.clearInterval(id)
    }
  }, [flush])

  const run = useCallback(
    async <T,>(label: string, method: QueuedMethod, path: string, body: unknown, opts?: RunOptions): Promise<KitchenActionResult<T>> => {
      // One key per intent: the caller's, or the waiting copy's when the same
      // request is already queued. A route with a waiting action is not sent
      // twice when the caller asks for that (exclusive).
      const plan = planRun(loadQueue(), method, path, body, opts)
      if (plan.kind === 'already-queued') return { status: 'queued' }
      const key = plan.key
      try {
        const res = await apiClient.inventoryFlowMutate<T>(method, path, body, key)
        if (loadQueue().some((a) => a.id === key)) {
          removeAction(key)
          setPending(loadQueue())
        }
        invalidate()
        return { status: 'sent', data: res.data as T }
      } catch (err) {
        if (!isRetryableFailure(err)) throw err
        const user = currentQueueUser()
        const server = !(err as FailureLike)?.isNetworkError
        enqueueAction({
          id: key, label, method, path, body, createdAt: Date.now(), attempts: 1,
          lastError: (err as FailureLike)?.message,
          userId: user?.id ?? '', userName: user?.name ?? '',
          ...(server ? { serverAttempts: 1, nextAttemptAt: Date.now() + backoffMs(1) } : {}),
        })
        setPending(loadQueue())
        return { status: 'queued' }
      }
    },
    [invalidate],
  )

  /** A failed action: back to waiting, and try it now. */
  const retry = useCallback(async (id: string) => {
    retryAction(id)
    setPending(loadQueue())
    await flushWith(true)
  }, [flushWith])

  /** Drop an action from this tablet (failed, or someone else's held one). */
  const discard = useCallback((id: string) => {
    removeAction(id)
    setPending(loadQueue())
    invalidate()
  }, [invalidate])

  const split = splitQueue(pending, currentQueueUser()?.id ?? '')
  return { run, pending, flush, flushNow, retry, discard, ...split }
}
